import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

/**
 * Filters the swatch library as the customer types. Each swatch card carries a
 * `data-swatch-search` attribute (name, code, and collection); cards that do
 * not match every word of the query are hidden, collections with no visible
 * cards are hidden with them, and a "no results" message appears when nothing
 * matches.
 *
 * A `has-query` attribute on the host element reflects whether the field has
 * text; CSS keys the clear button's visibility off it.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} input - The search field
 * @property {HTMLButtonElement} [clear] - Button that empties the field
 * @property {HTMLElement} [noResults] - Message shown when nothing matches
 *
 * @extends {Component<Refs>}
 */
class SwatchLibrarySearch extends Component {
  requiredRefs = ['input'];

  /** Reflect the query state and apply it to the swatch grids. */
  filter = () => {
    this.toggleAttribute('has-query', this.refs.input.value.trim().length > 0);
    this.#applyFilter();
  };

  /** Empty the field, restore every swatch, and refocus the input. */
  clear = () => {
    this.refs.input.value = '';
    this.filter();
    this.refs.input.focus();
  };

  #applyFilter = debounce(() => {
    const query = this.refs.input.value.trim().toLowerCase();
    const terms = query.split(/\s+/).filter(Boolean);
    let anyVisible = false;

    for (const collection of this.querySelectorAll('.section-swatch-library__collection')) {
      let visibleCount = 0;

      for (const card of collection.querySelectorAll('.swatch-card-block')) {
        const haystack = (card.getAttribute('data-swatch-search') ?? '').toLowerCase();
        const matches = terms.every((term) => haystack.includes(term));

        card.toggleAttribute('hidden', !matches);
        if (matches) visibleCount += 1;
      }

      collection.toggleAttribute('hidden', visibleCount === 0);
      if (visibleCount > 0) anyVisible = true;
    }

    this.refs.noResults?.toggleAttribute('hidden', anyVisible);
  }, 100);
}

if (!customElements.get('swatch-library-search')) {
  customElements.define('swatch-library-search', SwatchLibrarySearch);
}
